import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Phone, PhoneOff, PhoneIncoming, Pause, Move, X, Hash } from 'lucide-react'
import toast from 'react-hot-toast'
import { get, patch } from '@/lib/api'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'
import { formatCountdown } from '@/lib/format'
import { useAuthStore } from '@/stores/auth'
import { useSocketEvent } from '@/lib/socket'

type State = 'AVAILABLE' | 'BUSY' | 'WRAP_UP' | 'OFFLINE' | 'BREAK'

interface Session {
  id: string
  externalId: string
  startedAt: string
  endedAt: string | null
  durationSec: number | null
  fromNumber: string | null
  toNumber: string | null
  disposition: string | null
}

interface ActiveCall {
  sessionId: string
  startedAt: number
  contactName: string | null
  contactId: string | null
}

const STATE_COLORS: Record<State, string> = {
  AVAILABLE: 'bg-green',
  BUSY:      'bg-amber',
  WRAP_UP:   'bg-muted',
  OFFLINE:   'bg-muted',
  BREAK:     'bg-soft',
}

const STATE_LABELS: Record<State, string> = {
  AVAILABLE: 'Available',
  BUSY:      'Busy',
  WRAP_UP:   'Wrap-up',
  OFFLINE:   'Offline',
  BREAK:     'Break',
}

/**
 * Bottom-right embedded softphone. One small pill when collapsed, slides up
 * to a 280px panel when expanded. Agent state, active call, keypad, and the
 * last 5 sessions from /telephony/sessions. Live updates via Socket.io.
 */
export function OmniSoftphone() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<State>('OFFLINE')
  const [active, setActive] = useState<ActiveCall | null>(null)
  const [held, setHeld] = useState(false)
  const [now, setNow] = useState(Date.now())
  const [keypadOpen, setKeypadOpen] = useState(false)
  const [dialled, setDialled] = useState('')

  // Poll own availability state from the server so the pill stays honest even
  // if another tab changed it. The PATCH mutation updates optimistically.
  const avail = useQuery({
    queryKey: ['agent', 'me', 'state'],
    queryFn: () => get<Array<{ id: string; state: State }>>('/api/v1/agents/availability', { background: true }),
    enabled: !!user,
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  useEffect(() => {
    if (!user || !avail.data) return
    const me = avail.data.find((a) => a.id === user.id)
    if (me) setState(me.state)
  }, [avail.data, user])

  const sessions = useQuery({
    queryKey: ['telephony', 'sessions', 'recent'],
    queryFn: () => get<Session[]>('/api/v1/telephony/sessions', { query: { limit: 5, sort: 'startedAt', order: 'desc' } }),
    staleTime: 30_000,
  })

  const updateState = useMutation({
    mutationFn: (next: State) => patch<{ state: State }>('/api/v1/agents/me/state', { state: next }),
    onMutate: (next) => {
      const prev = state
      setState(next)
      return { prev }
    },
    onError: (err: Error, _next, ctx) => {
      if (ctx) setState(ctx.prev as State)
      toast.error(err.message)
    },
    onSettled: () => { qc.invalidateQueries({ queryKey: ['agent', 'me', 'state'] }) },
  })

  // Active-call handling — opens the panel automatically and requests an OS
  // notification so agents don't miss inbound rings.
  useSocketEvent('omni:call_start', (event) => {
    setActive({
      sessionId: event.sessionId,
      startedAt: Date.now(),
      contactName: null,
      contactId: event.contactId ?? null,
    })
    setOpen(true)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Incoming call', { body: event.sessionId })
    }
  })
  useSocketEvent('omni:call_end', () => {
    setActive(null)
    setHeld(false)
    qc.invalidateQueries({ queryKey: ['telephony', 'sessions', 'recent'] })
  })

  // 1-second tick while a call is active.
  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [active])

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {})
    }
  }, [])

  const dot = STATE_COLORS[state]
  const activeMs = active ? now - active.startedAt : 0
  const keypadKeys = useMemo(() => ['1','2','3','4','5','6','7','8','9','*','0','#'], [])

  if (!user) return null

  return (
    <div className="fixed bottom-6 right-6 z-[55] flex flex-col items-end gap-2">
      {open ? (
        <div className="w-[280px] bg-card rounded-card shadow-elevated border border-border overflow-hidden" role="dialog" aria-label="Softphone">
          <header className="bg-violet text-white px-3 py-2 flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-xs font-semibold">
              <Phone className="h-3.5 w-3.5" /> Softphone
            </span>
            <button onClick={() => setOpen(false)} aria-label="Collapse softphone" className="h-6 w-6 flex items-center justify-center rounded hover:bg-white/10">
              <X className="h-3.5 w-3.5" />
            </button>
          </header>

          <div className="px-3 py-3 border-b border-border">
            <div className="label-xs mb-1">My state</div>
            <div className="flex items-center gap-1">
              {(['AVAILABLE', 'BUSY', 'WRAP_UP'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => updateState.mutate(s)}
                  className={cn(
                    'flex-1 h-8 rounded-input text-xs font-semibold transition-colors',
                    state === s ? 'ring-2 ring-violet/40' : '',
                    s === 'AVAILABLE' ? 'bg-green-tint text-[#065F46]' :
                    s === 'BUSY'      ? 'bg-amber-tint text-amber-text' :
                                        'bg-page text-muted',
                  )}
                >
                  {STATE_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {active ? (
            <div className="px-3 py-3 border-b border-border space-y-2">
              <div className="flex items-center gap-2">
                <Avatar name={active.contactName ?? active.sessionId} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{active.contactName ?? 'Unknown caller'}</div>
                  <div className="font-mono text-xs text-muted">{formatCountdown(activeMs)}</div>
                </div>
                {held ? <span className="text-xs text-amber-text font-semibold">HOLD</span> : null}
              </div>
              <div className="flex items-center gap-1">
                <Tooltip label="Hold">
                  <Button variant="secondary" size="sm" onClick={() => setHeld((h) => !h)}>
                    <Pause className="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
                <Tooltip label="Transfer">
                  <Button variant="secondary" size="sm"><Move className="h-3.5 w-3.5" /></Button>
                </Tooltip>
                <Button variant="danger" size="sm" className="ml-auto" onClick={() => setActive(null)}>
                  <PhoneOff className="h-3.5 w-3.5" /> End
                </Button>
              </div>
            </div>
          ) : null}

          {keypadOpen ? (
            <div className="px-3 py-3 border-b border-border">
              <input
                aria-label="Dial number"
                className="w-full h-9 px-2 border border-border rounded-input text-sm mb-2 font-mono"
                placeholder="Enter number…"
                value={dialled}
                onChange={(e) => setDialled(e.target.value)}
              />
              <div className="grid grid-cols-3 gap-1">
                {keypadKeys.map((k) => (
                  <button
                    key={k}
                    onClick={() => setDialled((d) => d + k)}
                    className="h-9 bg-page rounded-input text-sm font-mono hover:bg-violet-tint"
                  >{k}</button>
                ))}
              </div>
              <Button className="w-full mt-2" size="sm" onClick={() => toast('Dial not wired yet')}>
                <Phone className="h-3.5 w-3.5" /> Call
              </Button>
            </div>
          ) : null}

          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-2">
              <div className="label-xs">Recent</div>
              <button onClick={() => setKeypadOpen((k) => !k)} className="text-[11px] text-violet inline-flex items-center gap-1 hover:underline">
                <Hash className="h-3 w-3" /> Keypad
              </button>
            </div>
            {sessions.isLoading ? (
              <div className="text-xs text-muted">Loading…</div>
            ) : (sessions.data ?? []).length === 0 ? (
              <div className="text-xs text-muted">No calls yet.</div>
            ) : (
              <ul className="space-y-1">
                {sessions.data!.slice(0, 5).map((s) => (
                  <li key={s.id} className="flex items-center justify-between text-xs py-1">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <PhoneIncoming className="h-3 w-3 text-muted" />
                      <span className="truncate font-mono">{s.fromNumber ?? s.externalId}</span>
                    </span>
                    <span className="text-muted whitespace-nowrap">{s.disposition ?? '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      <button
        onClick={() => setOpen((v) => !v)}
        className="h-[52px] w-[52px] rounded-full bg-green text-white shadow-elevated flex items-center justify-center relative hover:bg-green-hover"
        aria-label={open ? 'Collapse softphone' : 'Open softphone'}
      >
        <Phone className="h-5 w-5" />
        <span className={cn('absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white', dot)} />
      </button>
    </div>
  )
}
